import { gql } from 'graphql-request';
import { Show, createEffect, onCleanup, onMount, useContext } from 'solid-js';
import { AppContext } from '../../app-context-provider/app-context-provider';
import type { HeaderData, LinkType, PrimaryMenuData } from './menu-types';
import { MenuMobile } from './menu-mobile/menu-mobile';
import { ErrorCatcher } from '../../tools/error-catcher';

const GET_HEADER_QUERY = gql`
    query GetHeader($key: String!) {
        header: resource(key: $key) {
            content
            key
        }
    }
`;

export const GET_PRIMARY_MENU_QUERY = gql`
    query GetPrimaryMenu($key: String!) {
        menu: resource(key: $key) {
            content
            key
        }
    }
`;

export const Menu = () => {
    const { createCachedResource, appStore } = useContext(AppContext);

    onMount(() => {
        onCleanup(() => cleanupEscapeListener());
    });

    createEffect(() => {
        if (menuOpen()) {
            document.addEventListener('keydown', escapeListener);
        } else {
            cleanupEscapeListener();
        }
    });

    const escapeListener = (e: KeyboardEvent) => {
        if (e.key === 'Escape') close();
    };

    const cleanupEscapeListener = () => {
        document.removeEventListener('keydown', escapeListener);
    };

    const getMenuLocation = () => (appStore.isHcp() ? 'primary_menu_hcp' : 'primary_menu');

    const [headerContent] = createCachedResource(GET_HEADER_QUERY, () => ({ key: '/wp-json/rawb/v1/header' }), true);
    const [primaryMenuContent] = createCachedResource(GET_PRIMARY_MENU_QUERY, () => ({ key: `/wp-json/content/v1/menus/${getMenuLocation()}` }), true);
    const { menuOpen, close } = appStore;

    const loaded = () => Boolean(headerContent());

    const headerData = (): HeaderData => headerContent()?.header?.content;
    const primaryMenuData = (): PrimaryMenuData => primaryMenuContent()?.menu?.content;

    /* Utility row (Contact / Blog / Events). Audience-aware. */
    const utilityLinks = (): LinkType[] => {
        const data = headerData();
        if (!data) {
            return [];
        }
        if (appStore.isHcp()) {
            return [data.headerHcpContactLink, data.headerHcpBlogsLink, data.headerHcpEventsLink].filter(Boolean) as LinkType[];
        }
        return [data.headerContactLink, data.headerBlogsLink, data.headerEventsLink].filter(Boolean) as LinkType[];
    };

    return (
        <ErrorCatcher componentName="Menu">
            <Show when={menuOpen()}>
                <div class="[container-type:inline-size] fixed inset-0 z-100 bg-cvi-bg-body overflow-y-auto overflow-x-hidden">
                    <Show when={loaded()} fallback={<h2>Could not find content</h2>}>
                        <MenuMobile
                            headerLinks={headerData()?.headerLinks}
                            utilityLinks={utilityLinks()}
                            handleCloseMenuClick={() => close()}
                            primaryMenuLinks={primaryMenuData()?.menu}
                            primaryMenuName={primaryMenuData()?.menuName}
                        />
                    </Show>
                </div>
            </Show>
        </ErrorCatcher>
    );
};
